// Row selection for every Asset Management table that has checkboxes (Assets,
// and the Network tab's per-entity-type tables).
//
// THE INVENTORY PATTERN, not a new one. BatteryPackListTab /
// UnlinkedIOTDevicesTab / AggregatedMetricsTable keep two pieces of state --
// `isSelectAll` and the explicitly ticked rows -- and the list-action request
// carries `resourceIDs` + `selectAll`. The server ignores resourceIDs when
// selectAll is true and narrows to them otherwise, so the file is scoped in ONE
// place and the client never filters the export itself.
//
// The state is ONE object so a transition is one update:
//
//   { isSelectAll: boolean, rows: [{ id, ...whatever toRow keeps }] }
//
// `rows` holds objects rather than bare ids because the Assets tab needs the
// serial and custody of a ticked row on a page the user has since left; the
// Network tab keeps `{ id }` alone.
//
// Transitions (all pure, all tested):
//
//   header ticked            -> select-all mode. Every row on every page
//                               renders ticked, pages visited later included,
//                               and the count is the filtered total.
//   a row unticked while in  -> select-all ends and the OTHER rows of the page
//   select-all mode             on screen stay ticked. That is exactly what
//                               AggregatedMetricsTable / Inventory do: their
//                               ticked keys are the page's rows at the moment
//                               the header was ticked, minus the one unticked.
//                               There are no exclusions: "all but these" is not
//                               something resourceIDs can express.
//   header unticked          -> in select-all mode: everything cleared.
//                               Otherwise (a page the user ticked row by row,
//                               which antd then draws with a ticked header):
//                               only THIS page's rows are removed, so picks on
//                               other pages survive -- the table's existing
//                               cross-page behaviour.
//   Clear / filter / search  -> EMPTY_SELECTION, through the callers' existing
//   / sort / tab switch         clearing paths.
//   page change              -> nothing changes. Select-all keeps covering every
//                               page; explicit ticks survive paging as before.

export const EMPTY_SELECTION = Object.freeze({ isSelectAll: false, rows: [] });

const hasID = (record) =>
    record !== null &&
    record !== undefined &&
    record.id !== null &&
    record.id !== undefined;

const DEFAULT_TO_ROW = (record) => ({ id: record.id });

const asList = (value) => (Array.isArray(value) ? value : []);

const normalise = (selection) => ({
    isSelectAll: Boolean(selection?.isSelectAll),
    rows: asList(selection?.rows),
});

// One row's checkbox.
export const selectionAfterRowToggle = (
    selection,
    record,
    selected,
    { pageRecords = [], toRow = DEFAULT_TO_ROW } = {}
) => {
    const current = normalise(selection);
    if (!hasID(record)) return current;

    if (current.isSelectAll) {
        // Already covered: every row is ticked in this mode.
        if (selected) return current;
        return {
            isSelectAll: false,
            rows: asList(pageRecords)
                .filter((row) => hasID(row) && row.id !== record.id)
                .map(toRow),
        };
    }

    // Deduplicated by id: antd can fire onSelect for a row already in the list.
    const without = current.rows.filter((row) => row.id !== record.id);
    return {
        isSelectAll: false,
        rows: selected ? [...without, toRow(record)] : without,
    };
};

// The header checkbox. `changeRows` is antd's third onSelectAll argument -- the
// rows of THIS page whose state actually changed.
export const selectionAfterHeaderToggle = (
    selection,
    selected,
    { pageRecords = [], changeRows = [], toRow = DEFAULT_TO_ROW } = {}
) => {
    const current = normalise(selection);

    if (selected) {
        // The page's rows are kept as the "real" selection, like
        // BatteryPackListTab: GenericDraggableTable asks getTotalRowSelection()
        // whether anything is selected before warning that a filter change
        // will drop it, and an empty list would skip that warning in exactly
        // the case where the most is lost.
        return {
            isSelectAll: true,
            rows: asList(pageRecords).filter(hasID).map(toRow),
        };
    }

    if (current.isSelectAll) return { ...EMPTY_SELECTION, rows: [] };

    const cleared = new Set(asList(changeRows).filter(hasID).map((row) => row.id));
    return {
        isSelectAll: false,
        rows: current.rows.filter((row) => !cleared.has(row.id)),
    };
};

// What antd's `selectedRowKeys` gets: in select-all mode every id on the page
// on screen, so a page opened later is drawn ticked too.
export const selectedKeysForPage = (selection, pageRecords = []) => {
    const current = normalise(selection);
    if (current.isSelectAll) {
        return asList(pageRecords).filter(hasID).map((row) => row.id);
    }
    return current.rows.map((row) => row.id);
};

// "N selected". The filtered total in select-all mode.
export const selectionCount = (selection, total) => {
    const current = normalise(selection);
    if (current.isSelectAll) {
        const n = Number(total);
        return Number.isFinite(n) && n > 0 ? n : 0;
    }
    return current.rows.length;
};

export const hasSelection = (selection, total) => selectionCount(selection, total) > 0;

// The ids GenericDraggableTable reads to decide whether to warn before a
// filter/search/sort change. Never empty while something is selected.
export const selectionRowIDs = (selection) =>
    normalise(selection).rows.map((row) => row.id);

// The two fields every list-action / export request carries -- the Inventory
// contract. resourceIDs is sent EMPTY in select-all mode rather than as the
// page's ids: the server ignores it then, and sending the page would invite a
// reader to think the file is that page.
//
// Nothing selected = { resourceIDs: [], selectAll: false } = the whole filtered
// set, which is what the tables' always-enabled Download has always meant.
export const selectionRequestFields = (selection) => {
    const current = normalise(selection);
    return {
        resourceIDs: current.isSelectAll ? [] : current.rows.map((row) => row.id),
        selectAll: current.isSelectAll,
    };
};

// The backend's ceiling on resourceIDs per list-action / export request
// (evcell: max 1000 ids; a longer list is a 400). Select-all sends no ids, so
// it is never capped -- that is the way to act on more rows than this.
export const MAX_RESOURCE_IDS = 1000;

export const RESOURCE_IDS_CAP_MESSAGE = `Select up to ${MAX_RESOURCE_IDS} rows, or use select all`;

// "" when the request may go out, otherwise the message to toast INSTEAD of
// sending a request the server will reject.
export const selectionCapError = (selection, cap = MAX_RESOURCE_IDS) => {
    const { resourceIDs } = selectionRequestFields(selection);
    return resourceIDs.length > cap ? RESOURCE_IDS_CAP_MESSAGE : "";
};
